'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils/tw-merge';

type HeroVideoProps = {
    src: string;
    mimeType?: string;
};

/**
 * Sources this tab has already played through once. A hero returned to by
 * client-side navigation mounts a brand new element with an empty readyState
 * even when the file is sitting in cache, so the element itself cannot tell us
 * the footage is a known quantity — only the session can.
 */
const revealed = new Set<string>();

/**
 * The hero's background footage, carrying no poster on purpose: a still would
 * paint first and then be swapped out under the viewer. Instead the stage holds
 * the page background and the video fades up the moment its first frame exists,
 * which reads as an opening rather than a pop.
 *
 * Seen once, it stops being an entrance: coming back to the page shows the
 * footage outright, because a fade that plays on every visit is just a delay.
 *
 * The only part of the hero that hydrates besides the showreel button — the
 * element itself is still server-rendered, so the video starts fetching from the
 * initial HTML rather than waiting on the bundle.
 */
export function HeroVideo({ src, mimeType }: HeroVideoProps) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [reveal, setReveal] = useState<'pending' | 'fade' | 'instant'>(() =>
        revealed.has(src) ? 'instant' : 'pending',
    );

    // The first frame can land before this hydrates, and then there is no event
    // left to catch — readyState is the only record that it already happened.
    useEffect(() => {
        const video = videoRef.current;
        if (video && video.readyState >= video.HAVE_CURRENT_DATA) {
            revealed.add(src);
            setReveal((current) => (current === 'pending' ? 'fade' : current));
        }
    }, [src]);

    return (
        <video
            ref={videoRef}
            className={cn(
                'hero__media hero__media--motion',
                reveal === 'fade' && 'is-ready',
                reveal === 'instant' && 'is-instant',
            )}
            onLoadedData={() => {
                revealed.add(src);
                setReveal((current) => (current === 'pending' ? 'fade' : current));
            }}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
        >
            <source src={src} type={mimeType} />
        </video>
    );
}
